import React from 'react';
import { LayoutGrid, List, Search } from 'lucide-react';
import { Select } from '../ui';
import {
    RfqStatusFilter, RfqStageFilter, RfqOwnerFilter, RfqSort, RfqStatusCounts, DEFAULT_SORT,
} from '../../hooks/useRfqList';
import { ACTIVE_PIPELINE_STAGES } from '../../hooks/dashboardMappers';
import { Segmented } from './rfqDisplay';

/**
 * Filter bar for the /rfqs page — every control is backed by a stored field
 * on the list item (name/id text search, status, stage, ownership, sort).
 * The sort select appears in card view only: in table view the column
 * headers own sorting, so the two controls never fight over one state.
 */
export type RfqListView = 'cards' | 'table';

interface RfqListToolbarProps {
    search: string;
    onSearch: (value: string) => void;
    status: RfqStatusFilter;
    onStatus: (value: RfqStatusFilter) => void;
    statusCounts: RfqStatusCounts;
    stage: RfqStageFilter;
    onStage: (value: RfqStageFilter) => void;
    owner: RfqOwnerFilter;
    onOwner: (value: RfqOwnerFilter) => void;
    sort: RfqSort;
    onSort: (value: RfqSort) => void;
    view: RfqListView;
    onView: (value: RfqListView) => void;
}

/** Card-view sort presets — each maps to one concrete field + direction. */
const SORT_PRESETS: { value: string; label: string; sort: RfqSort }[] = [
    { value: 'updated-desc', label: 'Recently updated', sort: DEFAULT_SORT },
    { value: 'created-desc', label: 'Newest created', sort: { key: 'created', direction: 'desc' } },
    { value: 'created-asc', label: 'Oldest created', sort: { key: 'created', direction: 'asc' } },
    { value: 'name-asc', label: 'Name A–Z', sort: { key: 'name', direction: 'asc' } },
];

export const presetOf = (sort: RfqSort): string | undefined =>
    SORT_PRESETS.find((p) => p.sort.key === sort.key && p.sort.direction === sort.direction)?.value;

/**
 * Cards or table. Hidden on a phone, where the table is not one of the two:
 * its five columns at 375px leave a project name about 60px to live in, so
 * the list is always cards there and the switch would be an offer to make
 * the page worse.
 */
const ViewToggle: React.FC<{ view: RfqListView; onView: (v: RfqListView) => void }> = ({ view, onView }) => (
    <span role="group" aria-label="View" className="inline-flex overflow-hidden rounded-control border border-line max-md:hidden">
        {([
            { value: 'cards', label: 'Card view', icon: <LayoutGrid className="h-[13px] w-[13px]" /> },
            { value: 'table', label: 'Table view', icon: <List className="h-[13px] w-[13px]" /> },
        ] as const).map((opt, i) => (
            <button
                key={opt.value}
                type="button"
                onClick={() => onView(opt.value)}
                aria-pressed={view === opt.value}
                aria-label={opt.label}
                title={opt.label}
                className={`flex cursor-pointer items-center px-[10px] py-[6px] transition-colors ${i > 0 ? 'border-l border-line' : ''} ${
                    view === opt.value ? 'bg-action-bg text-action' : 'bg-surface text-neutral-ink hover:bg-neutral-bg'
                }`}
            >
                {opt.icon}
            </button>
        ))}
    </span>
);

const RfqListToolbar: React.FC<RfqListToolbarProps> = ({
    search, onSearch, status, onStatus, statusCounts,
    stage, onStage, owner, onOwner, sort, onSort, view, onView,
}) => (
    <div className="flex flex-wrap items-center gap-x-[14px] gap-y-s3 rounded-card border border-line bg-surface px-s6 py-[12px] max-md:px-s5">
        <span className="relative w-[220px] max-w-full">
            <Search className="pointer-events-none absolute left-s3 top-1/2 h-[13px] w-[13px] -translate-y-1/2 text-ink-muted" />
            <input
                type="search"
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Search by name or RFQ id"
                aria-label="Search RFQs by name or id"
                className="h-8 w-full rounded-control border border-line bg-surface pl-[26px] pr-s3 text-body-sm text-ink placeholder:text-ink-muted transition-colors hover:border-line-strong focus:border-action focus:outline-none focus:ring-2 focus:ring-action-border"
            />
        </span>
        <Segmented<RfqStatusFilter>
            label="Status"
            value={status}
            onChange={onStatus}
            options={[
                { value: 'all', label: `All · ${statusCounts.all}` },
                { value: 'active', label: `Active · ${statusCounts.active}` },
                { value: 'awarded', label: `Awarded · ${statusCounts.awarded}` },
                { value: 'cancelled', label: `Cancelled · ${statusCounts.cancelled}` },
            ]}
        />
        <Select
            aria-label="Filter by stage"
            value={stage}
            onChange={(e) => onStage(e.target.value as RfqStageFilter)}
            className="h-8 w-auto min-w-[130px] py-0 text-body-sm"
            options={[
                { value: 'all', label: 'All stages' },
                ...ACTIVE_PIPELINE_STAGES.map((s) => ({ value: s, label: s })),
            ]}
        />
        <div className="ml-auto flex flex-wrap items-center gap-s3">
            <Segmented<RfqOwnerFilter>
                label="Owner"
                value={owner}
                onChange={onOwner}
                options={[
                    { value: 'everyone', label: 'Everyone' },
                    { value: 'mine', label: 'My projects' },
                ]}
            />
            {view === 'cards' && (
                <Select
                    aria-label="Sort RFQs"
                    value={presetOf(sort) ?? 'updated-desc'}
                    onChange={(e) => {
                        const preset = SORT_PRESETS.find((p) => p.value === e.target.value);
                        if (preset) onSort(preset.sort);
                    }}
                    className="h-8 w-auto min-w-[150px] py-0 text-body-sm"
                    options={SORT_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
                />
            )}
            <ViewToggle view={view} onView={onView} />
        </div>
    </div>
);

export default RfqListToolbar;
